import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SquaresFour, Buildings, UsersThree, FileText, SignOut, Cube, Trophy, UserGear, ClockCounterClockwise, List, X, Stack, Lock, Clock, ShieldCheck, WarningCircle, Gear } from "@phosphor-icons/react";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: SquaresFour },
  { to: "/projects", label: "Projects", icon: Buildings },
  { to: "/vendors", label: "Vendors", icon: UsersThree },
  { to: "/cost-library", label: "Cost Library", icon: Stack },
  { to: "/quotations", label: "Quotations", icon: FileText },
  { to: "/best-price", label: "Best Price", icon: Trophy },
  { to: "/team", label: "Team", icon: UserGear },
  { to: "/activity", label: "Activity", icon: ClockCounterClockwise },
  { to: "/settings", label: "Settings", icon: Gear },
];

export default function Layout({ children }) {
  const { user, subscription, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showNudge, setShowNudge] = useState(false);
  const [apiDown, setApiDown] = useState(false);
  const [logo, setLogo] = useState(null);

  useEffect(() => {
    api.get("/team/branding").then((r) => {
      if (r.data.has_logo) setLogo(`${API}/team/branding/logo`);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/health`;
    let active = true;
    const check = async () => {
      try {
        const res = await fetch(url, { method: "GET" });
        const data = await res.json().catch(() => ({}));
        if (active) setApiDown(!res.ok || data.database !== "up");
      } catch {
        if (active) setApiDown(true);
      }
    };
    check();
    const id = setInterval(check, 30000);
    return () => { active = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    if (subscription?.trial && subscription.days_left <= 2 && subscription.days_left >= 0 && subscription.until) {
      const key = `bt_trial_nudge_${subscription.until}`;
      if (!localStorage.getItem(key)) setShowNudge(true);
    }
  }, [subscription]);

  const dismissNudge = () => {
    if (subscription?.until) localStorage.setItem(`bt_trial_nudge_${subscription.until}`, "1");
    setShowNudge(false);
  };

  useEffect(() => {
    const onPaywall = (e) => {
      toast.error(e.detail || "Subscribe to unlock this feature.", {
        action: { label: "Subscribe", onClick: () => navigate("/pricing") },
      });
    };
    window.addEventListener("subscription-required", onPaywall);
    return () => window.removeEventListener("subscription-required", onPaywall);
  }, [navigate]);

  const previewMode = subscription && subscription.active === false;
  const trialMode = subscription && subscription.trial === true;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SidebarContent = (
    <>
      <div className="h-16 flex items-center justify-between gap-2 px-6 border-b border-border">
        <NavLink to="/dashboard" onClick={() => setOpen(false)} data-testid="sidebar-brand-home"
          className="flex items-center gap-2 group" aria-label="Go to dashboard">
          <Cube size={26} weight="bold" className="transition-transform duration-200 group-hover:scale-110" />
          <span className="font-heading font-black text-lg tracking-tighter group-hover:text-primary/80 transition-colors">BuildTrack</span>
        </NavLink>
        {logo && (
          <img src={logo} alt={user?.team_name || "Company"} data-testid="sidebar-company-logo"
            className="max-h-8 max-w-[90px] object-contain" />
        )}
      </div>
      <nav className="flex-1 py-6 overflow-y-auto">
        {nav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => setOpen(false)}
            data-testid={`nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-6 py-3 text-sm font-medium transition-all duration-200 border-l-2 ${
                isActive
                  ? "border-primary bg-secondary text-primary"
                  : "border-transparent text-muted-foreground hover:bg-secondary hover:text-primary hover:translate-x-1"
              }`
            }
          >
            <item.icon size={20} weight="regular" />
            {item.label}
          </NavLink>
        ))}
        {subscription?.is_superadmin && (
          <NavLink
            to="/admin/subscriptions"
            onClick={() => setOpen(false)}
            data-testid="nav-admin-subscriptions"
            className={({ isActive }) =>
              `flex items-center gap-3 px-6 py-3 text-sm font-medium transition-all duration-200 border-l-2 ${
                isActive
                  ? "border-primary bg-secondary text-primary"
                  : "border-transparent text-muted-foreground hover:bg-secondary hover:text-primary hover:translate-x-1"
              }`
            }
          >
            <ShieldCheck size={20} weight="regular" />
            Subscriptions
          </NavLink>
        )}
      </nav>
      <div className="border-t border-border p-4">
        <div className="px-2 mb-3">
          <div className="text-sm font-semibold truncate" data-testid="user-name">{user?.name}</div>
          <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="flex items-center gap-2 w-full px-2 py-2 text-sm text-muted-foreground hover:text-destructive transition-colors duration-200"
        >
          <SignOut size={18} /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 border-r border-border flex-col fixed h-screen bg-background z-20">
        {SidebarContent}
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 h-14 border-b border-border bg-background flex items-center justify-between px-4">
        <NavLink to="/dashboard" onClick={() => setOpen(false)} data-testid="mobile-brand-home"
          className="flex items-center gap-2" aria-label="Go to dashboard">
          <Cube size={22} weight="bold" />
          <span className="font-heading font-black tracking-tighter">BuildTrack</span>
        </NavLink>
        <div className="flex items-center gap-3">
          {logo && (
            <img src={logo} alt={user?.team_name || "Company"} data-testid="mobile-company-logo"
              className="max-h-7 max-w-[80px] object-contain" />
          )}
          <button onClick={() => setOpen(true)} data-testid="mobile-menu-button" aria-label="Open menu" className="p-2 -mr-2">
            <List size={24} />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-72 max-w-[85%] bg-background border-r border-border flex flex-col animate-in slide-in-from-left">
            <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu"
              className="absolute top-4 right-4 z-10 p-1 text-muted-foreground hover:text-primary">
              <X size={22} />
            </button>
            {SidebarContent}
          </aside>
        </div>
      )}

      <main className="lg:ml-64 min-h-screen">
        {apiDown && (
          <div className="bg-destructive text-destructive-foreground px-4 py-2.5 flex items-center gap-2 text-sm" data-testid="service-unavailable-banner">
            <WarningCircle size={16} weight="bold" />
            <span><strong>Service temporarily unavailable.</strong> We're having trouble reaching the server — your data is safe. This will clear automatically once the connection is restored.</span>
          </div>
        )}
        {previewMode && (
          <div className="bg-primary text-primary-foreground px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid="preview-banner">
            <div className="flex items-center gap-2 text-sm">
              <Lock size={16} weight="bold" />
              <span><strong>Preview mode.</strong> You can look around, but creating & editing is locked. Subscribe to unlock BuildTrack.</span>
            </div>
            <button onClick={() => navigate("/pricing")} data-testid="preview-banner-upgrade"
              className="text-sm font-semibold bg-primary-foreground text-primary px-3 py-1 hover:opacity-90 transition-opacity shrink-0">
              View plans →
            </button>
          </div>
        )}
        {trialMode && (
          <div className="bg-amber-500 text-black px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid="trial-banner">
            <div className="flex items-center gap-2 text-sm">
              <Lock size={16} weight="bold" />
              <span><strong>Free trial — {subscription.days_left} day{subscription.days_left === 1 ? "" : "s"} left.</strong> Subscribe now to keep full access when your trial ends.</span>
            </div>
            <button onClick={() => navigate("/pricing")} data-testid="trial-banner-upgrade"
              className="text-sm font-semibold bg-black text-white px-3 py-1 hover:opacity-90 transition-opacity shrink-0">
              View plans →
            </button>
          </div>
        )}
        {children}
      </main>

      <Dialog open={showNudge} onOpenChange={(o) => { if (!o) dismissNudge(); }}>
        <DialogContent className="rounded-none max-w-md" data-testid="trial-nudge-modal">
          <div className="text-center py-2">
            <div className="w-14 h-14 mx-auto bg-amber-500 text-black flex items-center justify-center mb-4">
              <Clock size={28} weight="bold" />
            </div>
            <h2 className="font-heading text-2xl font-black tracking-tight">
              {subscription?.days_left <= 0 ? "Your trial ends today" : `${subscription?.days_left} day${subscription?.days_left === 1 ? "" : "s"} left in your trial`}
            </h2>
            <p className="text-muted-foreground text-sm mt-3">
              Keep full access to your projects, cost tracking, AI extraction and Cost Library. Subscribe before your trial ends — everything stays exactly where you left it.
            </p>
            <div className="flex flex-col gap-2 mt-6">
              <Button onClick={() => { dismissNudge(); navigate("/pricing"); }} className="w-full rounded-none h-11" data-testid="trial-nudge-subscribe">
                View plans & subscribe
              </Button>
              <button onClick={dismissNudge} data-testid="trial-nudge-dismiss" className="text-sm text-muted-foreground hover:text-primary">
                Maybe later
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
